import React, { FunctionComponent, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import AddOnComponent from './AddOnComponent';
import AddOnService from '@spike/addon-service-model';
import { AddOnServicesStatus, fetchThunk as fetchAddOnServicesThunk } from '@spike/addon-services-action';
import useNonInitialEffect from '@versiondos/hooks';
import { Spinner } from 'components/UI';
import { Moment } from 'moment-timezone';
import { AddOnDate } from 'model/NewMultipleBooking';

interface Props {
    changedDate?: boolean;
    date?: Moment;
    dates?: Array<AddOnDate>;
    selectedDates?: Array<Moment>;
    addOnsUsed?: Array<number>;
    selectedAddOn?: AddOnService;
    petBusinessAreas?: Array<string>;
    onSelectAddOn: (addOn?: AddOnService) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            paddingBottom: 18,
            [theme.breakpoints.up('md')]: {
                paddingBottom: 20
            }
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            marginBottom: 18,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        }
    })
);

export const AddOnSelector: FunctionComponent<Props> = props => {
    const classes = useStyles(props);
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const addOnServices = useSelector<RootState, Array<AddOnService>>(state => state.addOnServices.services);
    const serviceStatus = useSelector<RootState, AddOnServicesStatus>(state => state.addOnServices.status);
    const [options, setOptions] = useState<Array<AddOnService>>([]);
    const [optionSelected, setOptionSelected] = useState<AddOnService | undefined>(props.selectedAddOn);
    const [loading, setLoading] = useState<boolean>(true);

    const matchesPetBusinessAreas = (addOnService: AddOnService) => {
        const areas = addOnService.businessAreas ?? [];
        if (areas.length === 0 || props.petBusinessAreas === undefined) {
            return true;
        }
        return areas.some(area => props.petBusinessAreas!.includes(area));
    };

    const getAddOns = () => {
        const addOnFiltered = addOnServices.filter(
            addOnService => addOnService.active && matchesPetBusinessAreas(addOnService)
        );
        if (props.selectedDates?.length) {
            const filteredDates = props.dates?.filter(date =>
                props.selectedDates?.some(selectedDate => date.date === selectedDate.format('L'))
            );
            setOptions(getAddOnsUnused(addOnFiltered, filteredDates, props.selectedDates));
            return;
        }

        const filteredDates = props.dates?.filter(date => date.date === props.date?.format('L'));
        setOptions(getAddOnsUnused(addOnFiltered, filteredDates));
    };

    const cleanName = (name: string) =>
        name
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z\s]/g, '')
            .toLowerCase()
            .trim();

    const getAddOnsUnused = (
        opts: Array<AddOnService>,
        datesSelected?: Array<AddOnDate>,
        selectedDates?: Array<Moment>
    ) => {
        if (props.addOnsUsed) {
            const addOnsToSelect: Array<AddOnService> = [];
            opts.forEach(adds => {
                const addOnFounded = props.addOnsUsed?.find(a => a === adds?.id);
                const isUsedOnCurrentDate = datesSelected?.find(
                    a => a.id === adds?.id && a.date === props.date?.format('L')
                );
                const isUsedOnAnySelectedDate = selectedDates?.some(selectedDate =>
                    props.dates?.some(a => a.id === adds?.id && a.date === selectedDate.format('L'))
                );

                if (selectedDates?.length) {
                    if (!isUsedOnAnySelectedDate) {
                        addOnsToSelect.push(adds);
                    }
                } else if (!addOnFounded || (addOnFounded && !isUsedOnCurrentDate)) {
                    addOnsToSelect.push(adds);
                }

                if (props.changedDate && isUsedOnCurrentDate) {
                    props.onSelectAddOn && props.onSelectAddOn(undefined);
                    setOptionSelected(undefined);
                }
            });
            return addOnsToSelect.sort((wrapper, otherWrapper) =>
                cleanName(wrapper.name).localeCompare(cleanName(otherWrapper.name))
            );
        }
        return opts;
    };

    useNonInitialEffect(() => {
        getAddOns();
    }, [props.date, props.selectedDates]);

    useNonInitialEffect(() => {
        switch (serviceStatus) {
            case AddOnServicesStatus.Fetching:
                setLoading(true);
                break;
            case AddOnServicesStatus.FetchSuccess:
                setLoading(false);
                getAddOns();
                break;
            case AddOnServicesStatus.Error:
                setLoading(false);
        }
    }, [serviceStatus]);

    useEffect(() => {
        dispatch(fetchAddOnServicesThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectHandler = (option: AddOnService) => {
        const selectedAddOn = addOnServices.find(addOnService => addOnService.id === option.id);
        setOptionSelected(option);
        props.onSelectAddOn && props.onSelectAddOn(selectedAddOn);
    };

    return (
        <Box className={classes.container}>
            {loading ? (
                <Spinner />
            ) : (
                <AddOnComponent
                    selectedOption={optionSelected}
                    options={options}
                    onSelectAddOn={selectHandler}
                />
            )}
        </Box>
    );
};

export default AddOnSelector;
